import { TreeItemDragAndDropOverlay } from '@mui/x-tree-view';
import { TreeItemIconContainer } from '@mui/x-tree-view/TreeItem';
import { TreeItemProvider } from '@mui/x-tree-view/TreeItemProvider';
import { useTreeItemModel } from '@mui/x-tree-view/hooks';
import {
  useTreeItem,
  UseTreeItemParameters,
} from '@mui/x-tree-view/useTreeItem';
import * as React from 'react';
import { CustomCheckbox } from '../CustomCheckbox';
import {
  describeInstrument,
  effectiveDateOf,
  LedgerItem,
  partiesOf,
} from '../ledger/ledgerInstrument';
import {
  getSignatureState,
  SignatureChip,
  SignatureFacts,
} from '../SignatureChip';
import { Caret } from './Caret';
import { CustomLabel } from './CustomLabel';
import { LabelAlerts } from './LabelAlerts';
import { LedgerDate } from './LedgerDate';
import { LedgerFile } from './LedgerFile';
import { LedgerParties } from './LedgerParties';
import { TreeItemGroup } from './TreeItemGroup';
import '../ledger/TreeLedger.scss';
import './CustomTreeItem.scss';

type ExtendedTreeItemProps = LedgerItem & {
  fileData?: SignatureFacts & NonNullable<LedgerItem['fileData']>;
  folderData?: NonNullable<LedgerItem['folderData']> & { status?: string };
  /** Cited but not held ("missing"), or skipped in the numbering ("gap"). */
  missing?: { reason?: 'missing' | 'gap' };
  /** On a contract row: how many of each it lacks. */
  missingCount?: number;
  gapCount?: number;
};

/** Where, in viewport coordinates, a row's menu should open. */
export interface RowMenuPoint {
  x: number;
  y: number;
}

/**
 * Where a menu opened from the keyboard sits: under the row's caret, on the
 * side the row reads from, since there is no pointer to open it at.
 */
const keyboardMenuPoint = (row: HTMLElement): RowMenuPoint => {
  const box = (
    row.querySelector('.tree-ledger-twisty') ?? row
  ).getBoundingClientRect();
  const rtl = getComputedStyle(row).direction === 'rtl';
  return { x: rtl ? box.right : box.left, y: box.bottom };
};

/** The keys that open a context menu: the menu key, and Shift+F10. */
const isMenuKey = (event: React.KeyboardEvent) =>
  event.key === 'ContextMenu' || (event.shiftKey && event.key === 'F10');

export interface CustomTreeItemProps
  extends Omit<UseTreeItemParameters, 'rootRef'>,
    Omit<React.HTMLAttributes<HTMLLIElement>, 'onFocus'> {
  /**
   * Opens the row's menu: on right-click at the pointer, or from the keyboard
   * (the menu key or Shift+F10) under the row.
   */
  onRightClick?: (
    event: React.SyntheticEvent,
    nodeId: string,
    at: RowMenuPoint
  ) => void;
  /** Open in the viewer. */
  isSelected: boolean;
  showCheckbox?: boolean;
  /** At the end of the name column, after the name and its tags */
  labelAdornment?: React.ReactNode;
  /** In the actions column, after the date (e.g. the contract graph link) */
  preStatusAdornment?: React.ReactNode;
  /** Last in the alerts column, after any alerts (e.g. a section's expand toggle) */
  endAdornment?: React.ReactNode;
}

/**
 * One row of the explorer, drawn as a row of the Consolidation Ledger: the
 * name (caret, checkbox, glyph, id and name), then the file's own name where
 * the row is named by the document's title, Party A and Party B, the
 * effective date, the contract graph link and the alerts, with a section's
 * expand toggle last in the alerts column (see
 * ../ledger/_columns.scss, which also decides how many columns fit).
 *
 * A folder being renamed shows its name as a field: the tree's host supplies
 * the rename through `TreeRenameProvider` rather than through each row.
 *
 * The columns are filled from the folder listing alone, so they hold what it
 * carries. A date or party it lacks reads as the ledger's dash.
 */
const CustomTreeItemRow = React.forwardRef(function CustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const {
    id,
    itemId,
    label,
    disabled,
    children,
    onRightClick,
    isSelected,
    showCheckbox = true,
    labelAdornment,
    preStatusAdornment,
    endAdornment,
    ...other
  } = props;

  const {
    getContextProviderProps,
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getCheckboxProps,
    getLabelProps,
    getGroupTransitionProps,
    getDragAndDropOverlayProps,
    status,
  } = useTreeItem({ id, itemId, children, label, disabled, rootRef: ref });

  const item = useTreeItemModel<ExtendedTreeItemProps>(itemId);
  const isFile = item?.fileType !== 'folder';
  const signatureState = getSignatureState(item?.fileData);
  // The File column repeats nothing: a row named by its file shows it once.
  const filename = item?.fileData?.filename;
  const fileColumnName = filename !== label ? filename : undefined;

  const { isInstrument, effective, parties } = React.useMemo(
    () => ({
      isInstrument: describeInstrument(item ?? undefined).isInstrument,
      effective: item ? effectiveDateOf(item) : null,
      parties: item ? partiesOf(item) : [],
    }),
    [item]
  );

  /* What the rail down the row's edge reports. A document the chain lacks —
     the row's own, or one under a contract row — comes before the processing
     status, missing before a gap; only a failure outranks them. */
  const processingStatus =
    item?.fileData?.status?.code ?? item?.folderData?.status ?? 'none';
  const ledgerStatus = item?.missing
    ? item.missing.reason ?? 'missing'
    : processingStatus === 'failed'
    ? 'failed'
    : item?.missingCount
    ? 'missing'
    : item?.gapCount
    ? 'gap'
    : processingStatus;

  // A plain class list instead of an `sx` prop: `sx` re-serialises styles for
  // every row on every render, which is the single most expensive thing a
  // tree row can do when hundreds of them are mounted at once.
  const surfaceClassName = [
    'tree-item-surface',
    isSelected && 'tree-item-surface--selected',
    `tree-item-content-${label}`,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <TreeItemProvider {...getContextProviderProps()}>
      {/* Its own class, as on the caret box: MUI puts none on an item it
            doesn't style itself, so its focus ring had nothing to hang on. */}
      <li
        {...getRootProps({
          ...other,
          className: other.className
            ? `tree-ledger-item ${other.className}`
            : 'tree-ledger-item',
          // Through getRootProps, so the tree's own arrow-key handling still
          // runs after this one.
          onKeyDown: (event: React.KeyboardEvent<HTMLLIElement>) => {
            other.onKeyDown?.(event);
            if (!onRightClick || !isMenuKey(event)) return;
            // A field in the row (the rename input) keeps its own keys.
            if ((event.target as HTMLElement).closest('input, textarea'))
              return;
            event.preventDefault();
            event.stopPropagation();
            onRightClick(event, itemId, keyboardMenuPoint(event.currentTarget));
          },
        })}
        onContextMenu={(event) => {
          event.preventDefault();
          event.stopPropagation();
          // The browser fires this for the menu key too, with no pointer
          // position: open it under the row then, as the keydown does.
          const fromPointer = event.clientX !== 0 || event.clientY !== 0;
          onRightClick?.(
            event,
            itemId,
            fromPointer
              ? { x: event.clientX, y: event.clientY }
              : keyboardMenuPoint(event.currentTarget)
          );
        }}
      >
        <div className={surfaceClassName}>
          <div
            {...getContentProps()}
            data-testid={`tree-item-content-${itemId}`}
            data-name={isFile ? 'file' : 'folder'}
            data-node-label={label as string}
            data-ledger-status={ledgerStatus}
            className="file tree-ledger-row"
          >
            {/* Its own class: MUI's styled container carries only a
                  generated one, which the ledger's styles can't select. */}
            <TreeItemIconContainer
              {...getIconContainerProps()}
              className="tree-ledger-twisty"
              data-testid={`tree-item-icon-${itemId}`}
              data-tree-item-id={itemId}
              data-name={isFile ? 'file' : 'folder'}
              data-node-label={label as string}
            >
              {status.expandable && <Caret />}
            </TreeItemIconContainer>
            <div
              className="tree-item-check w-4 h-4 flex items-center"
              data-testid={`tree-item-checkbox-${itemId}`}
            >
              {showCheckbox && <CustomCheckbox {...getCheckboxProps()} />}
            </div>
            {signatureState && (
              <SignatureChip
                state={signatureState}
                signatures={item?.fileData?.signature_info}
              />
            )}
            <CustomLabel
              {...getLabelProps()}
              itemId={itemId}
              item={item}
              adornment={labelAdornment}
            />
            {item?.fileData && <LedgerFile name={fileColumnName} />}
            {isInstrument && (
              <>
                <LedgerParties names={parties} />
                <LedgerDate value={effective} />
              </>
            )}
            {preStatusAdornment && (
              <span className="tree-label-actions">{preStatusAdornment}</span>
            )}
            <LabelAlerts
              item={item ?? undefined}
              name={String(label ?? '')}
              expanded={status.expandable && status.expanded}
              isInstrument={isInstrument}
              endAdornment={endAdornment}
            />
            <TreeItemDragAndDropOverlay {...getDragAndDropOverlayProps()} />
          </div>
        </div>
        {children && <TreeItemGroup {...getGroupTransitionProps()} />}
      </li>
    </TreeItemProvider>
  );
});

export const CustomTreeItem = React.memo(CustomTreeItemRow);
